/** Epoch ms → a compact local timestamp, or a dash for "never recorded". */
export function formatEpoch(ms: number | null | undefined): string {
	if (ms === null || ms === undefined) return "—";

	return new Date(ms).toLocaleString(undefined, {
		year: "2-digit",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
}

/** Milliseconds → "1m 23s" / "450ms". Null stays a dash: no data is not zero. */
export function formatMs(ms: number | null | undefined): string {
	if (ms === null || ms === undefined) return "—";
	if (ms < 1000) return `${Math.round(ms)}ms`;

	const totalSeconds = Math.round(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

/**
 * Seconds → a clock: "0:48", "12:05", or "3:04:17" past an hour.
 *
 * Talk time is sold and billed in seconds, so "0.8 min" hides the number the
 * pulse rounds. A clock shows it, and reads the same in a single call's row
 * and in a day's total.
 */
export function formatClock(seconds: number): string {
	const total = Math.max(0, Math.round(seconds));
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = String(total % 60).padStart(2, "0");

	return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/**
 * Integer micros of a currency → a readable amount.
 *
 * ## Why micros exist in the first place
 *
 * A per-character vendor price is a very small number and a month of calls is
 * a very large count. Floating point loses money in that combination, quietly,
 * so every amount crossing the wire is an integer millionth of one currency
 * unit. This is the only place that stops being true, and only to draw it.
 *
 * ## Why null stays a dash
 *
 * **Null is not zero.** A leg nothing could price is a missing number, and
 * rendering it as ₹0.00 turns "we do not know what this cost" into "this was
 * free" — the exact failure that makes Sentry's own model-cost column unusable
 * for this platform. Every caller here has a null case, and it must keep one.
 *
 * ## Why so many decimals on a small number
 *
 * A single call's synthesis costs fractions of a paisa, and rounding a vendor
 * rate to two decimals renders most of this page as ₹0.00. Amounts below one
 * unit get four decimal places so a real difference is visible; above it, two,
 * because nobody needs a tenth of a paisa on a ₹4,000 month.
 */
export function formatMicros(micros: number | null | undefined, currency = "INR"): string {
	if (micros === null || micros === undefined) return "—";

	const units = micros / 1_000_000;
	const decimals = Math.abs(units) > 0 && Math.abs(units) < 1 ? 4 : 2;

	try {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency,
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}).format(units);
	} catch {
		// An unrecognised ISO code throws rather than degrading, and a price book
		// an operator types into can contain one. The code beside the number is
		// still an honest answer; a crashed page is not.
		return `${units.toFixed(decimals)} ${currency}`;
	}
}

/**
 * A per-unit vendor rate, which is a much smaller number than an amount.
 *
 * ₹0.003 per character rounds to ₹0.00 at two decimals and to ₹0.0030 at four,
 * so rates are drawn from the micros themselves with up to six decimals —
 * enough to show every integer-micro rate exactly, which is the only precision
 * the book can actually hold.
 */
export function formatRate(micros: number, currency: string): string {
	const units = micros / 1_000_000;

	return `${units.toFixed(6).replace(/0+$/, "").replace(/\.$/, "")} ${currency}`;
}

/** A large count with thousands separators. `1234567` → `1,234,567`. */
export function formatCount(value: number | null | undefined): string {
	if (value === null || value === undefined) return "—";

	return new Intl.NumberFormat().format(value);
}
